import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../auth/authStore';
import { gql } from '../../../graphql/client';
import { M_CREATE_FRONTEND_APP, Q_LIST_FRONTEND_APPS } from '../../../graphql/operations';
import type { FrontendApp } from '../../../types/forge';

export function AppListScreen() {
  const orgId = useAuthStore((s) => s.defaultOrgId);
  const clearSession = useAuthStore((s) => s.clearSession);
  const userEmail = useAuthStore((s) => s.user?.email);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const apps = useQuery({
    queryKey: ['apps', orgId],
    enabled: !!orgId,
    queryFn: async () =>
      gql<{ FrontendApp: FrontendApp[] }>(Q_LIST_FRONTEND_APPS, { orgId }),
  });

  const createApp = useMutation({
    mutationFn: async () =>
      gql<{ insert_FrontendApp_one: FrontendApp }>(M_CREATE_FRONTEND_APP, {
        name,
        type: 'app',
      }),
    onSuccess() {
      setName('');
      queryClient.invalidateQueries({ queryKey: ['apps', orgId] });
    },
  });

  return (
    <div style={styles.shell}>
      <header style={styles.header}>
        <h1 style={styles.title}>Forge Builder</h1>
        <div style={styles.headerRight}>
          <span style={styles.userEmail}>{userEmail}</span>
          <button onClick={clearSession} style={styles.logoutBtn}>Sign out</button>
        </div>
      </header>

      <main style={styles.main}>
        <section style={styles.createCard}>
          <h2 style={styles.h2}>Create a new app</h2>
          <form
            style={styles.createRow}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) createApp.mutate();
            }}
          >
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My new app"
              style={styles.input}
            />
            <button type="submit" disabled={!name.trim() || createApp.isPending} style={styles.button}>
              {createApp.isPending ? 'Creating…' : 'Create'}
            </button>
          </form>
        </section>

        <section>
          <h2 style={styles.h2}>Your apps</h2>
          {apps.isLoading && <p>Loading…</p>}
          {apps.error && <p style={{ color: '#c00' }}>Failed to load apps</p>}
          {apps.data && (
            <ul style={styles.appList}>
              {apps.data.FrontendApp.map((app) => (
                <li key={app.id} style={styles.appItem}>
                  <Link to={`/apps/${app.id}`} style={styles.appLink}>
                    <span style={styles.appName}>{app.name}</span>
                    <span style={styles.appMeta}>{app.type}</span>
                  </Link>
                </li>
              ))}
              {apps.data.FrontendApp.length === 0 && (
                <li style={styles.appEmpty}>No apps yet — create one above.</li>
              )}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: { minHeight: '100vh', backgroundColor: '#F8FAFC', fontFamily: 'system-ui, sans-serif' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    borderBottom: '1px solid #E2E8F0',
    backgroundColor: '#fff',
  },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  userEmail: { fontSize: 13, color: '#64748B' },
  logoutBtn: {
    padding: '6px 12px', fontSize: 12, border: '1px solid #CBD5E1',
    borderRadius: 6, backgroundColor: '#fff', cursor: 'pointer',
  },
  main: { maxWidth: 720, margin: '32px auto', padding: '0 24px' },
  createCard: { padding: 16, backgroundColor: '#fff', borderRadius: 8, marginBottom: 24, border: '1px solid #E2E8F0' },
  h2: { fontSize: 14, fontWeight: 600, color: '#475569', marginTop: 0, marginBottom: 12 },
  createRow: { display: 'flex', gap: 8 },
  input: { flex: 1, padding: '8px 12px', fontSize: 14, border: '1px solid #CBD5E1', borderRadius: 6 },
  button: {
    padding: '8px 16px', fontSize: 14, fontWeight: 600, color: '#fff',
    backgroundColor: '#4F46E5', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  appList: { listStyle: 'none', padding: 0, margin: 0 },
  appItem: { backgroundColor: '#fff', borderRadius: 8, marginBottom: 8, border: '1px solid #E2E8F0' },
  appLink: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', textDecoration: 'none',
  },
  appName: { fontSize: 14, fontWeight: 600, color: '#0F172A' },
  appMeta: { fontSize: 12, color: '#94A3B8' },
  appEmpty: { padding: 16, fontSize: 13, color: '#94A3B8', textAlign: 'center' },
};
